import { useMemo, useState } from "react";
import { InputFieldProps } from "./types";
import { ArrayInput } from "./ArrayInput";
import { EnumInput } from "./EnumInput";
import { StringInput } from "./StringInput";
import { NumberInput } from "./NumberInput";
import { BooleanInput } from "./BooleanInput";
import { ObjectInput } from "./ObjectInput";
import { LineItemInput } from "./LineItemInput";
import { OrganizationIdInput } from "./OrganizationIdInput";
import { TpaInput } from "./TpaInput";
import { CarrierInput } from "./CarrierInput";
import { CatCodeInput } from "./CatCodeInput";
import { QuantityFunctionInput } from "./QuantityFunctionInput";
import { RangeInput } from "./RangeInput";

/**
 * This component is responsible for updating a single field in a parent object. This component accepts the entire parent object, an `input field` that
 * defines which field in the parent that this component is responsible for updating, and an `onUpdate` function that updates the parent object based
 * on the user input.
 *
 * The `inputField` parameter also defines the its corresponding field's type and possible values.
 *
 * This component can be called recursively, please use the `path` variable to know where in the recursive tree an instance of this component is in.
 */
export const InputFieldComponent: React.FC<InputFieldProps> = ({ objectInput, inputField, inputValue, path, onUpdate: _onUpdate, validationErrors }) => {
	const [showDescription, setShowDescription] = useState(false);

	const onUpdate = (inputFieldValue: any) => {
		if (!!inputFieldValue || inputFieldValue === 0) {
			_onUpdate?.({ ...inputValue, [inputField.name]: inputFieldValue });
		} else {
			// if inputFieldValue is undefined, remove that field from the inputValue
			const { [inputField.name]: _, ...rest } = inputValue;
			_onUpdate?.(rest);
		}
	};

	const inputSelectionComponent = useMemo(() => {
		const props = { objectInput, inputField, inputValue: inputValue[inputField.name], path, onUpdate, validationErrors };

		if (inputField.type === "array") {
			return <ArrayInput key={path} {...props} />;
		}
		if (inputField.type === "enum") {
			return <EnumInput key={path} {...props} />;
		}
		if (inputField.type === "string") {
			return <StringInput key={path} {...props} />;
		}
		if (inputField.type === "number") {
			return <NumberInput key={path} {...props} />;
		}
		if (inputField.type === "boolean") {
			return <BooleanInput key={path} {...props} />;
		}
		if (inputField.type === "object") {
			return <ObjectInput key={path} {...props} />;
		}
		if (inputField.type === "lineItem") {
			return <LineItemInput key={path} {...props} />;
		}
		if (inputField.type === "organizations") {
			return <OrganizationIdInput key={path} {...props} />;
		}
		if (inputField.type === "tpas") {
			return <TpaInput key={path} {...props} />;
		}
		if (inputField.type === "carriers") {
			return <CarrierInput key={path} {...props} />;
		}
		if (inputField.type === "catCodes") {
			return <CatCodeInput key={path} {...props} />;
		}
		if (inputField.type === "quantityFunction") {
			return <QuantityFunctionInput key={path} {...props} />;
		}
		if (inputField.type === "range") {
			return <RangeInput key={path} {...props} />;
		}

		return <div>Unsupported parameter type</div>;
	}, [inputField, inputValue, onUpdate, path, inputField.name]);

	const parameterName = useMemo(
		() =>
			inputField.name
				.replace(/([A-Z])/g, " $1")
				.replace(/^./, (s) => s.toUpperCase())
				.trim(),
		[inputField.name],
	);

	// validation error keys are relative to ruleInputs (e.g. "child.overrideExistingLineItem"), but paths here are
	// rooted at the template's inputField.name (e.g. "ruleInputs.child.overrideExistingLineItem"), so we strip the first segment
	const relativePath = path.split(".").slice(1).join(".");
	const validationErrorMessage = validationErrors?.get(relativePath);

	// We want to hide the top-level input field's name since it deosn't really add anything to the UI
	return inputField.type === "object" && inputField.name === path ? (
		<div className="w-[100%] px-2">{inputSelectionComponent}</div>
	) : (
		<div className={`flex flex-row w-full gap-4 items-start rounded-md p-2 ${validationErrorMessage ? "bg-red-50 border border-red-300" : ""}`}>
			<div className="flex w-64 flex-col">
				<div className="flex flex-row justify-between">
					<div className="text-gray-700 text-md font-bold">{parameterName}</div>
					{/* tool-tip component that shows the input field's description */}
					{inputField.description && (
						<div className="relative inline-block">
							<span
								onMouseEnter={() => setShowDescription(true)}
								onMouseLeave={() => setShowDescription(false)}
								className="cursor-help w-4 h-4 rounded-full bg-gray-300 inline-flex items-center justify-center text-xs"
							>
								?
							</span>
							{showDescription && (
								<div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-3 bg-gray-800 text-white rounded-md text-sm max-w-md w-max z-10">
									<p className="whitespace-normal">{inputField.description}</p>
									<div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
								</div>
							)}
						</div>
					)}
				</div>
				{validationErrorMessage && <div className="mt-1 text-xs text-red-600">{validationErrorMessage.validationError}</div>}
			</div>
			<div className="w-[100%] px-2">{inputSelectionComponent}</div>
		</div>
	);
};
